import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Registration } from '../models/registration';
import { Observable, of } from 'rxjs';
import { SynonymResult } from '../models/synonymResult';
import { HTTPCONSTANTS } from '../http/http-constants';
import { FeaturedInitials, InitialSearchResults } from '../models/initialsSearchResults';
import { PlatopediaPlate } from '../models/platopediaPlate';
import { MultiTermOption } from '../models/multiTermOption';
import { MultiTermPlate } from '../models/multiTermPlate';
import { NamePlateResponse } from '../models/namePlateResponse';
import { Country } from '../models/country';
import { BYOTermResponse } from '../models/byoTermResponse';
import { AltSearchOption } from '../models/altSearchOption';
import { environment } from 'src/environments/environment';
import { DatelessSearchResponse } from '../pages/dateless-search-page/dateless-search-page.component';
import { CompsResponse } from '../pages/create-auction/create-auction.component';
import { DVLAAuctionResp } from '../pages/dvla-auction-page/dvla-auction-page.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NewReleaseResult } from '../models/newReleaseResult';
import { RoiRegistration } from '../components/new-release-market-data/new-release-market-data.component';
import { UserService } from './user-service';
import { NamePopularity } from '../components/name-popularity-data/name-popularity-data.component';
import { RecentlySoldPlate } from '../price-history-chart/price-history-chart.component';
import { NumberOneResults } from '../pages/number-one-page/number-one-page.component';
import { SessionService } from './session-service';
import {call} from "three/examples/jsm/nodes/shadernode/ShaderNodeBaseElements";
import {NewReleaseDropdownOptions} from "../components/new-release-search/new-release-search.component";
import { LiveV2SearchResponse } from '../models/liveV2Search';
import { ShadowLogEntry } from '../models/shadowLog';
import { shapeLikeDev2 } from './extended-groups';

export class PredictiveSearch {
  constructor(public registration: string, public price: string) { }
}

export class SearchReqResponse {
  // `count` is an optional true total for grouped results whose rows are capped
  // server-side (dev2 extended-search); when absent, registrations.length is it.
  constructor(public id: string, public registrations: Registration[], public count?: number) { }
}

export class ExtendedSearchReqResponse {
  constructor(
    public version: string,
    public results: Registration[],
    // dev2 live_v2 extras: `count` is the true group total (results are capped
    // server-side ~100/group), `int_version` the internal pattern, `hasMore`
    // whether more rows exist beyond this page.
    public count?: number,
    public int_version?: string,
    public hasMore?: boolean,
  ) { }
}

export class PatternSearchResponse {
  public wildcard: string;
  public current: Registration[];
  public prefix: Registration[];
  public suffix: Registration[];
  public dateless: Registration[];

}

@Injectable({ providedIn: 'root' })
export class SearchService {
  private userLoaded: boolean = false;
  private waitingCallbacks: any[] = [];
  // Raw search
  private lastResults: SearchReqResponse;
  private lastSearch: string;

  // Synonym search
  private lastSynonymResults: SynonymResult[];
  private lastSynonymSearch: string;

  private useLocalStorage: boolean = false;//true && !environment.production;

  constructor(private httpClient: HttpClient, private snackbar: MatSnackBar, private userService: UserService, private sessionService: SessionService) {
    if (!this.sessionService.isLoggedIn()) {
      this.userLoaded = true;
      this.waitingCallbacks.forEach(c => c());

      return;
    }
    this.userService.fetchAllUserData(() => {
      this.userLoaded = true;
      this.waitingCallbacks.forEach(c => c());
    });
  }

  private UserCheckCallback(callback: any): void {
    if (!this.sessionService.isLoggedIn()) { callback(); } else {
      // dev2's results body doesn't read favourites/notifies, so never hold
      // search results back for the (slow) account calls there.
      if (this.userLoaded || this.isDev2) callback();
      else this.waitingCallbacks.push(callback);
    }
  }

  // The redesigned results body (every host since the release) doesn't wait on
  // favourites/notifies.
  private isDev2: boolean = true;

  public getNewReleaseWordOptions(year: string, callback: (results: NewReleaseDropdownOptions) => void): void {
    this.httpClient
      .get<NewReleaseDropdownOptions>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=controller-new-release-get-options&year=${year}`
      )
      .subscribe(
        (res: NewReleaseDropdownOptions) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public getNewReleaseWordResults(year: string, term: string, prefix: boolean, callback: (results: NewReleaseResult[]) => void): void {
    this.httpClient
      .get<NewReleaseResult[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=controller-new-release-results&year=${year}&term=${term}&prefix=${prefix ? 'true' : 'false'}`
      )
      .subscribe(
        (res: NewReleaseResult[]) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public getYesterdaySoldResults(
    callback: (results: RecentlySoldPlate[]) => void
  ): void {
    const url = `${HTTPCONSTANTS.dataApiProtocol}${HTTPCONSTANTS.dataApiBaseAddress}/MarketWatch/recently-sold`;
    this.httpClient.get<RecentlySoldPlate[]>(url).subscribe({
      next: (res) => callback(Array.isArray(res) ? res : []),
      error: (err) => {
        console.error('MarketWatch recently-sold (Live Feed) failed:', err?.status ?? err?.message ?? err, err?.url ?? url);
        callback([]);
      },
    });
  }

  public getSub200(
    callback: (results: Registration[]) => void
  ): void {
    this.httpClient
      .get<Registration[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=sub-200`
      )
      .subscribe(
        (_: Registration[]) => {
          callback(_);
        },
        () => {
          callback([]);
        }
      );
  }

  public getNumber1(
    callback: (results: NumberOneResults) => void
  ): void {
    this.httpClient
      .get<NumberOneResults>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=number-1`
      )
      .subscribe(
        (_: NumberOneResults) => {
          callback(_);
        },
        () => {
          callback(undefined);
        }
      );
  }

  // Canonical-base variant of getNumber1. The Number Ones register page must
  // always reach the real plato-search Lambda (Mode 2's dev2 sed-rewrite does
  // not implement `process=number-1`). Same canonical-pair mechanism as
  // searchDatelessLengthCanonical below.
  public getNumber1Canonical(
    callback: (results: NumberOneResults) => void
  ): void {
    this.httpClient
      .get<NumberOneResults>(
        `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}?process=number-1`
      )
      .subscribe(
        (_: NumberOneResults) => {
          callback(_);
        },
        () => {
          callback(undefined);
        }
      );
  }

  public getPredictiveSearch(
    search: string,
    callback: (results: PredictiveSearch[]) => void
  ): void {
    this.httpClient
      .get<PredictiveSearch[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=predictive-search&term=${search}`
      )
      .subscribe(
        (_: PredictiveSearch[]) => {
          callback(_);
        },
        () => {
          callback([]);
        }
      );
  }

  public getAltSearchTerms(
    search: string,
    callback: (results: AltSearchOption[]) => void
  ): void {
    this.httpClient
      .get<AltSearchOption[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=alt-search-options&term=${search}`
      )
      .subscribe(
        (_) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  }

  public getValuationPlates(
    registration: string,
    search: string,
    callback: (results: Registration[]) => void
  ): void {
    this.httpClient
      .get(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=valuation&registration=${registration}&term=${search}`
      )
      .subscribe(
        (_: Registration[]) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  }

  public extendedSearch(
    term: string,
    style: string,
    callback: (results: ExtendedSearchReqResponse[]) => void
  ): void {
    // dev2: extended-search (Current/Prefix pattern groups) now runs on the
    // Mode 2 live_v2 gateway (same gateway as search_v3), not the legacy plato
    // search. Returns grouped, pre-sorted (available-first), capped results with
    // a true `count` per group. Prod is unaffected (environment.prod.ts keeps
    // its own mode2 base; Perfect still uses the plato search gateway).
    // Release hosts blank mode2BaseAddress: they ask the plato search, as www
    // always has, and shape its uncapped groups the way DEV2's gateway does.
    const mode2 = !!(environment as any).mode2BaseAddress;
    const url = mode2
      ? `${HTTPCONSTANTS.mode2_protocol}${HTTPCONSTANTS.mode2_base}`
      : `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}`;
    this.httpClient
      .get(`${url}?process=extended-search&term=${term}&style=${style}`)
      .subscribe(
        (_: ExtendedSearchReqResponse[]) => {
          const groups = mode2 ? _ : shapeLikeDev2(_);
          this.UserCheckCallback(() => callback(groups));
        },
        () => {
          callback(null);
        }
      );
  }

  /**
   * Search V2 (projection-backed, live_v2). Hits the .NET API (NOT the plato-search Lambda).
   * The endpoint is feature-flagged server-side: when UseLiveV2 is OFF it returns
   * { enabled:false } and the caller must fall back to the legacy six-call search.
   * A transport error also yields null so the caller falls back.
   */
  public searchV2(
    search: string,
    limit: number,
    callback: (results: LiveV2SearchResponse | null) => void
  ): void {
    if (!environment.useLiveV2) { callback(null); return; }
    this.httpClient
      .get<LiveV2SearchResponse>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/v2?q=${encodeURIComponent(search)}&limit=${limit}`
      )
      .subscribe(
        (res: LiveV2SearchResponse) => {
          // enabled:false => flag off server-side => caller falls back to legacy.
          this.UserCheckCallback(() => callback(res && res.enabled ? res : null));
        },
        () => callback(null)
      );
  }

  public searchV3(
    search: string,
    callback: (results: SearchReqResponse) => void,
    thirdPartyOnly: boolean = true,
  ): void {
    this.httpClient
      .get<SearchReqResponse>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=search_v3&term=${search}&thirdPartyOnly=${thirdPartyOnly ? 'true' : 'false'}`
      )
      .subscribe(
        (res: SearchReqResponse) => {
          if (this.useLocalStorage) localStorage.setItem(search.toUpperCase(), JSON.stringify(res)); // only in dev
          this.UserCheckCallback(() => callback(res));
        },
        () => callback(null)
      );
  }


  public searchRaw(
    search: string,
    callback: (results: SearchReqResponse) => void
  ): void {
    if (
      this.lastSearch != null &&
      search.toUpperCase() == this.lastSearch.toUpperCase()
    ) {
      callback(this.lastResults);
      return;
    }

    if (
      this.useLocalStorage &&
      localStorage.getItem(search.toUpperCase()) != null
    ) {
      console.log('use local results');
      var _storedResults = localStorage.getItem(search.toUpperCase());
      this.UserCheckCallback(() => callback(JSON.parse(_storedResults)))
      return;
    }

    this.httpClient
      .get<SearchReqResponse>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=search_v2&term=${search}`
      )
      .subscribe(
        (res: SearchReqResponse) => {
          this.lastSearch = search;
          this.lastResults = res;
          if (this.useLocalStorage)
            localStorage.setItem(search.toUpperCase(), JSON.stringify(res)); // only in dev
          this.UserCheckCallback(() => callback(res));
        },
        () => callback(null)
      );
  }

  /**
   * Canonical-base variant of searchRaw (process=search_v2).
   *
   * searchRaw() above uses the standard plato_search base, which on the
   * platex-dev2 build is sed-rewritten to the Mode 2 gateway at Amplify build
   * time. To reach the REAL plato-search Lambda (the optimised Mode 1 fast
   * engine that production uses, at platoSearchCanonicalBaseAddress), dev2's
   * search flow must use this canonical pair, which UrlInterceptor leaves
   * untouched by the Mode 2 rewrite. No local-storage / lastSearch caching here
   * so it never collides with the legacy searchRaw cache. Returns null on error.
   */
  public searchRawCanonical(
    search: string,
    callback: (results: SearchReqResponse | null) => void
  ): void {
    this.httpClient
      .get<SearchReqResponse>(
        `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}?process=search_v2&term=${encodeURIComponent(search)}`
      )
      .subscribe(
        (res: SearchReqResponse) => {
          this.UserCheckCallback(() => callback(res));
        },
        () => callback(null)
      );
  }

  public searchAuction(
    search_criteria: string,
    callback: (results: DVLAAuctionResp) => void
  ): void {
    // Use new data API (EC2) for auction data only
    var url = `${HTTPCONSTANTS.dataApiProtocol}${HTTPCONSTANTS.dataApiBaseAddress}/DVLAAuction/search-dvla-auction`;
    if (search_criteria != null)
      url = `${HTTPCONSTANTS.dataApiProtocol}${HTTPCONSTANTS.dataApiBaseAddress}/DVLAAuction/search-dvla-auction/${encodeURIComponent(search_criteria)}`;
    this.httpClient.get<DVLAAuctionResp>(url).subscribe(
      (res: DVLAAuctionResp) => {
        callback(res);
      },
      () => callback(null)
    );
  }

  public searchInitials(
    initials: string,
    style: string,
    callback: (results: InitialSearchResults) => void
  ): void {
    this.httpClient
      .get<InitialSearchResults>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=initials&style=${style}&term=${initials}`
      )
      .subscribe(
        (res: InitialSearchResults) => {
          this.UserCheckCallback(() => callback(res));
        },
        () => callback(null)
      );
  }

  public featuredInitials(
    callback: (results: FeaturedInitials) => void
  ): void {
    this.httpClient
      .get<FeaturedInitials>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=featured-initials`
      )
      .subscribe(
        (res: FeaturedInitials) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public searchPerfect(
    term: string,
    callback: (results: Registration[]) => void
  ): void {
    this.httpClient
      .get<Registration[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=perfect&term=${term}`
      )
      .subscribe(
        (res: Registration[]) => {
          this.UserCheckCallback(() => callback(res));
        },
        () => callback(null)
      );
  }

  public searchPattern(
    term: string,
    callback: (results: PatternSearchResponse) => void
  ): void {
    this.httpClient
      .get<PatternSearchResponse>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=pattern&term=${term}`
      )
      .subscribe(
        (res: PatternSearchResponse) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public searchExact(
    term: string,
    callback: (results: Registration[]) => void
  ): void {
    this.httpClient
      .get<Registration[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=exact&term=${term}`
      )
      .subscribe(
        (res: Registration[]) => {
          this.UserCheckCallback(() => callback(res));
        },
        () => callback([])
      );
  }

  public searchDatelessLength(
    length: string,
    search_criteria: string,
    callback: (results: DatelessSearchResponse) => void
  ): void {
    this.httpClient
      .get<DatelessSearchResponse>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=dateless${search_criteria == '' ? '' : `&term=${search_criteria}`}&layout=${length}`
      )
      .subscribe(
        (res: DatelessSearchResponse) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  // Canonical-base variant of searchDatelessLength. plato-search Mode 2 (the
  // dev2 sed-rewrite target) does NOT implement `process=dateless` — it returns
  // an empty `{}` — so callers that must reach the real plato-search Lambda on
  // every environment (e.g. the plate-detail dateless arrangement cohort) use
  // this canonical pair instead. See http-constants.ts / environment.prod.ts
  // for the split-URL mechanism that dodges the Amplify sed.
  public searchDatelessLengthCanonical(
    length: string,
    search_criteria: string,
    callback: (results: DatelessSearchResponse) => void
  ): void {
    this.httpClient
      .get<DatelessSearchResponse>(
        `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}?process=dateless${search_criteria == '' ? '' : `&term=${search_criteria}`}&layout=${length}`
      )
      .subscribe(
        (res: DatelessSearchResponse) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public getNewReleaseName(
    year: string,
    callback: (results: NewReleaseResult[]) => void
  ): void {
    this.httpClient
      .get<NewReleaseResult[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=new-release-name&filters=${year}`
      )
      .subscribe(
        (res: NewReleaseResult[]) => {
          this.UserCheckCallback(() => callback(res))
        },
        () => callback(null)
      );
  }

  public getNewRelease(
    year: string,
    filter: string,
    callback: (results: NewReleaseResult[]) => void
  ): void {
    this.httpClient
      .get<NewReleaseResult[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=new-release&filters=new-${year}-release`
      )
      .subscribe(
        (res: NewReleaseResult[]) => {
          this.UserCheckCallback(() => callback(res))
        },
        () => callback(null)
      );
  }

  public getNamePopularity(
    callback: (results: NamePopularity[]) => void
  ): void {
    this.httpClient
      .get<NamePopularity[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=name-popularity`
      )
      .subscribe(
        (res: NamePopularity[]) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public getInitialPopularity(
    callback: (results: { letterFirst: NamePopularity[], numberFirst: NamePopularity[] }) => void
  ): void {
    this.httpClient
      .get<{ letterFirst: NamePopularity[], numberFirst: NamePopularity[] }>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=initials-popularity`
      )
      .subscribe(
        (res: { letterFirst: NamePopularity[], numberFirst: NamePopularity[] }) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public getNewReleaseROI(
    year: string,
    callback: (results: RoiRegistration[]) => void
  ): void {
    this.httpClient
      .get<RoiRegistration[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=full-roi&filters=${year}`
        //`${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=new-release-roi&filters=${year}`
      )
      .subscribe(
        (res: RoiRegistration[]) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public searchplatopedia(
    queryString: string,
    callback: (filters: string, results: PlatopediaPlate[]) => void
  ): void {
    this.httpClient
      .get<PlatopediaPlate[]>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/platopedia/words${queryString}`
      )
      .subscribe(
        (res: PlatopediaPlate[]) => {
          this.UserCheckCallback(() => callback(queryString, res))
        },
        () => callback(queryString, null)
      );
  }

  public deletePlateopediaWord(word: string, registration: string): void {
    this.httpClient
      .delete(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/platopedia/word/${word}/${registration}`
      )
      .subscribe(
        () => {
          this.snackbar.open(`Deleted ${word} from word list`);
        },
        () => {
          alert('failed to delete term');
        }
      );
  }

  public fetchMultiTermOptions(
    tag: string,
    callback: (results: MultiTermOption[]) => void
  ): void {
    this.httpClient
      .get<MultiTermOption[]>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/multi-term/options/${tag}`
      )
      .subscribe(
        (res: MultiTermOption[]) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public fetchMultiTermResults(
    term: string,
    callback: (results: MultiTermPlate[]) => void
  ): void {
    this.httpClient
      .get<MultiTermPlate[]>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/multi-term/${term}/results`
      )
      .subscribe(
        (res: MultiTermPlate[]) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public getNameCountries(callback: (results: Country[]) => void): void {
    this.httpClient
      .get<Country[]>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/name/countries`
      )
      .subscribe(
        (res: Country[]) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public fetchNamePlates(
    pageIndex: number,
    countryCode: string,
    firstCharacter: string,
    callback: (results: NamePlateResponse) => void
  ): void {
    this.httpClient
      .get<NamePlateResponse>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/name/results/${countryCode}/${firstCharacter}`,
        {
          headers: new HttpHeaders({
            pageIndex: pageIndex.toString(),
          }),
        }
      )
      .subscribe(
        (res: NamePlateResponse) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public hideNamePlate(id: string): void {
    this.httpClient
      .get<Registration[]>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/name/${id}/hide`
      )
      .subscribe(
        () => { },
        () => alert('error')
      );
  }

  public hideMTPlate(id: string): void {
    this.httpClient
      .get<Registration[]>(
        `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/multi-term/${id}/hide`
      )
      .subscribe(
        () => { },
        () => alert('error')
      );
  }

  public searchSynonym(
    search: string,
    callback: (results: SynonymResult[]) => void
  ): void {
    if (search == this.lastSynonymSearch) {
      callback(this.lastSynonymResults);
      return;
    }
    // dev2: Similar (synonym) runs on the PRODUCTION plato-search endpoint
    // (plato_search_base), same as production. The Mode 2 gateway returned a
    // byte-identical synonym response, so this is a zero-loss consolidation onto
    // the one production search system.
    this.httpClient
      .get<SynonymResult[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=synonym&term=${search}`
      )
      .subscribe(
        (res: SynonymResult[]) => {
          this.lastSynonymSearch = search;
          this.lastSynonymResults = res;
          callback(res);
        },
        () => callback(null)
      );
  }

  public searchBYO(
    style: string,
    prefix: string,
    numbers: string,
    letters: string,
    callback: (results: Registration[]) => void
  ): void {
    // Canonical placeholder — plato-search Mode 2 (the dev2 sed-rewrite target)
    // returns `[]` for `process=byo`. The legacy /build-your-own page relied on
    // a backend that pre-dated Mode 2, and the new /new-release/:year inline
    // BYO depends on the same call. Using the canonical pair keeps both
    // surfaces hitting the real plato-search Lambda on every environment.
    // See http-constants.ts and environment.prod.ts for the split-URL
    // mechanism that dodges the Amplify sed.
    this.httpClient
      .get<Registration[]>(
        `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}?process=byo&style=${style}&term=${prefix}/${numbers}/${letters}`
      )
      .subscribe(
        (res: Registration[]) => {
          callback(res);
        },
        () => callback([])
      );
  }

  private searchBYOViaPlato(
    style: string,
    prefix: string,
    numbers: string,
    letters: string,
    callback: (results: Registration[]) => void
  ): void {
    this.httpClient
      .get<Registration[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=byo&style=${style}&term=${encodeURIComponent(prefix + '/' + numbers + '/' + letters)}`
      )
      .subscribe(
        (byoResults: Registration[]) => {
          const term = `${prefix}${numbers}${letters}`.replace(/-/gi, '?');
          this.searchExact(encodeURIComponent(term), (exactResults: Registration[]) => {
            callback([...(exactResults || []), ...(byoResults || [])]);
          });
        },
        () => callback([])
      );
  }

  public getBYOTerm(term: string, callback: (result: BYOTermResponse) => void) {
    this.httpClient
      .get<BYOTermResponse>(
        `${HTTPCONSTANTS.platoProtocol}${HTTPCONSTANTS.platoBaseAddress}/byo-term/${term}`
      )
      .subscribe(
        (res: BYOTermResponse) => {
          callback(res);
        },
        () => callback(null)
      );
  }

  public scorePlate(plate: string, term: string): Observable<any> {
    return this.httpClient.get<any>(
      `${HTTPCONSTANTS.platoProtocol}${HTTPCONSTANTS.platoBaseAddress}/score/${plate}/${term}`
    );
  }

  /**
   * Mode 2 primary search — calls the production Mode 2 Lambda directly.
   * Returns a raw `SearchReqResponse`-shaped object from the Mode 2 handler
   * ({"id": uuid, "registrations": [...]}).
   *
   * When `environment.useLiveV2` is false, or when `mode2BaseAddress` is
   * empty, calls back with null so the caller can fall back to legacy.
   */
  public searchMode2(
    search: string,
    limit: number,
    callback: (results: SearchReqResponse | null) => void,
    thirdPartyOnly: boolean = true
  ): void {
    const base = (environment as any).mode2BaseAddress as string | undefined;
    if (!environment.useLiveV2 || !base) {
      callback(null);
      return;
    }
    this.httpClient
      .get<SearchReqResponse>(
        `${HTTPCONSTANTS.mode2_protocol}${HTTPCONSTANTS.mode2_base}?process=search_v3&term=${encodeURIComponent(search)}&thirdPartyOnly=${thirdPartyOnly}&limit=${limit}`
      )
      .subscribe(
        (res: SearchReqResponse) => {
          this.UserCheckCallback(() => callback(res));
        },
        () => callback(null)
      );
  }

  /**
   * Post a shadow comparison log entry to the shadow log Lambda.
   * Fire-and-forget — subscribe with an empty error handler.
   * Never called when `liveV2CompareEnabled` is false or `shadowLogBaseAddress` is empty.
   */
  public postShadowLog(entry: ShadowLogEntry): Observable<any> {
    const base = (environment as any).shadowLogBaseAddress as string | undefined;
    if (!base) { return of(null); }
    return this.httpClient.post(
      `${HTTPCONSTANTS.shadow_log_protocol}${HTTPCONSTANTS.shadow_log_base}`,
      entry
    );
  }

  /**
   * Patch an existing shadow log entry with click data.
   * Fire-and-forget.
   */
  public patchShadowLog(id: string, clicked: string, mode2Rank: number | null, legacyRank: number | null): Observable<any> {
    const base = (environment as any).shadowLogBaseAddress as string | undefined;
    if (!base || !id) { return of(null); }
    return this.httpClient.patch(
      `${HTTPCONSTANTS.shadow_log_protocol}${HTTPCONSTANTS.shadow_log_base}/${id}`,
      { clicked_registration: clicked, mode2_rank: mode2Rank, legacy_rank: legacyRank }
    );
  }
}
